import { omit } from "lodash";
import type { ComponentPropsWithRef, CSSProperties } from "react";
import { forwardRef } from "react";

import { t } from "@/i18n-js/instance";
import { ButtonSpinner } from "@/react/components/OnboardingPage/shared/ButtonSpinner";
import { Button } from "@/react/components/SettingsApp/CompassCompat/Button";
import { MarketingConsentCheckbox } from "@/react/components/SettingsApp/Emails/components/Forms/VisualFormsBuilder/components/MarketingConsentCheckbox";
import type { StyleValue } from "@/react/components/shared/BuilderUI/StyleValue.types";
import { parseStyles } from "@/react/components/shared/BuilderUI/utils/parseStyles";
import { classNames } from "@/react/helpers/twMergeWithCN";
import { useIsEmailHubNewsletterPhaseAEnabled } from "@/react/hooks/useIsEmailHubNewsletterPhaseAEnabled";

import { useBlocksContext } from "../../contexts/BlocksContext";
import { defaultStyles } from "./defaultStyles";

export interface FormButtonBlockProps extends ComponentPropsWithRef<"div"> {
  label: string;
  styles?: Readonly<Record<string, StyleValue>>;
}

export const FormButtonBlock = forwardRef<HTMLDivElement, FormButtonBlockProps>(
  (
    {
      label = t("settings.forms.default_submit"),
      className,
      styles = {},
      ...rest
    },
    ref,
  ) => {
    const elementProps = omit(rest, ["blockPath"]);
    const { useFormRenderingContext } = useBlocksContext();
    const context = useFormRenderingContext();
    const isNewsletterPhaseAEnabled = useIsEmailHubNewsletterPhaseAEnabled();
    const { buttonBackgroundColor, buttonTextColor, form, isPreview } = context;
    const isSubmitting =
      "isSubmitting" in context ? context.isSubmitting : false;

    const inlineStyles: CSSProperties = parseStyles({
      styles,
      defaultStyles,
    });
    return (
      <div
        ref={ref}
        className={classNames("mt-4 pb-2 shrink-0", className)}
        style={inlineStyles}
        {...elementProps}
      >
        {isNewsletterPhaseAEnabled &&
          form.email_marketing_enabled &&
          form.marketing_consent_checkbox_enabled && (
            <MarketingConsentCheckbox
              defaultChecked={form.marketing_consent_checkbox_default_checked}
              message={form.marketing_consent_checkbox_message}
            />
          )}
        <Button
          variant="circle"
          full
          large
          className="!h-auto !py-3"
          disabled={isSubmitting}
          type={isPreview ? "button" : "submit"}
          style={{
            color: buttonTextColor,
            backgroundColor: buttonBackgroundColor,
            borderColor: buttonBackgroundColor,
          }}
        >
          <span className="inline-flex items-center gap-2">
            {label}
            {isSubmitting && <ButtonSpinner />}
          </span>
        </Button>
      </div>
    );
  },
);

FormButtonBlock.displayName = "FormButtonBlock";
